/**
 * Session tracking for the retention dashboard (`session_started`).
 *
 * A "session" is one continuous stretch of use. It begins on app boot, and
 * again whenever the app returns to the foreground after being idle for
 * {@link SESSION_IDLE_MS}. Without the idle rule a native app — which almost
 * never reloads — would report a single session for its entire install
 * lifetime, and the retention curve would be flat and meaningless.
 *
 * Two timestamps live in `localStorage` (never `@capacitor/preferences`,
 * whose native calls hang in the Android WebView):
 *
 *   mash:last_session_at — start of the most recent session. Drives
 *                          `daysSinceLastSession`.
 *   mash:last_active_at  — when the app last went to the background. Drives
 *                          the idle check on the way back in.
 */

import mixpanel from "./mixpanel";

export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const LAST_SESSION_AT_KEY = "mash:last_session_at";
export const LAST_ACTIVE_AT_KEY = "mash:last_active_at";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Vocabulary fixed by the event-taxonomy design doc. */
export type SessionSource =
  | "iOS App"
  | "Android App"
  | "Mobile Web"
  | "Desktop Web";

export interface SessionEnvironment {
  isNative: boolean;
  /** Capacitor's platform id: "ios" | "android" | "web". */
  nativePlatform: string;
  isMobileViewport: boolean;
}

export function resolveSessionSource(env: SessionEnvironment): SessionSource {
  if (env.isNative) {
    if (env.nativePlatform === "ios") return "iOS App";
    if (env.nativePlatform === "android") return "Android App";
  }
  return env.isMobileViewport ? "Mobile Web" : "Desktop Web";
}

/** Whole days between two epoch-ms instants. Never negative — a clock that
 *  went backwards reports 0 rather than a nonsense negative retention bucket. */
export function daysBetween(fromMs: number, toMs: number): number {
  return Math.max(0, Math.floor((toMs - fromMs) / MS_PER_DAY));
}

/**
 * A fresh boot (`lastActiveAtMs === null`) always starts a session. A
 * foregrounded app starts one only after {@link SESSION_IDLE_MS} in the
 * background, so backgrounding for a few seconds mid-use doesn't inflate the
 * session count.
 */
export function shouldStartNewSession(
  lastActiveAtMs: number | null,
  nowMs: number,
  idleMs: number = SESSION_IDLE_MS,
): boolean {
  if (lastActiveAtMs === null) return true;
  return nowMs - lastActiveAtMs >= idleMs;
}

export interface SessionContext {
  sessionSource: SessionSource;
  /** `null` for a user's very first session — nothing to measure against. */
  daysSinceLastSession: number | null;
}

// The context of the session currently in progress. App.tsx reads this when
// binding user properties at identify time; recomputing there would return 0,
// since `trackSessionStarted` has already overwritten `last_session_at`.
let currentSession: SessionContext | null = null;

export function getCurrentSession(): SessionContext | null {
  return currentSession;
}

// localStorage throws in Safari private mode and when storage is full. A lost
// analytics timestamp must never take down app boot.
function readNumber(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* storage unavailable — skip */
  }
}

function detectEnvironment(): SessionEnvironment {
  const isMobileViewport =
    typeof window !== "undefined" && window.innerWidth < 768;
  // Read Capacitor off the global rather than importing it, so this module
  // stays a leaf that tests can exercise without the native shim.
  const cap = (
    globalThis as {
      Capacitor?: {
        isNativePlatform?: () => boolean;
        getPlatform?: () => string;
      };
    }
  ).Capacitor;
  return {
    isNative: cap?.isNativePlatform?.() ?? false,
    nativePlatform: cap?.getPlatform?.() ?? "web",
    isMobileViewport,
  };
}

export function trackSessionStarted(now: number = Date.now()): void {
  const lastSessionAt = readNumber(LAST_SESSION_AT_KEY);
  const context: SessionContext = {
    sessionSource: resolveSessionSource(detectEnvironment()),
    daysSinceLastSession:
      lastSessionAt === null ? null : daysBetween(lastSessionAt, now),
  };
  currentSession = context;

  writeNumber(LAST_SESSION_AT_KEY, now);
  writeNumber(LAST_ACTIVE_AT_KEY, now);

  mixpanel.track("session_started", {
    sessionSource: context.sessionSource,
    ...(context.daysSinceLastSession !== null && {
      daysSinceLastSession: context.daysSinceLastSession,
    }),
  });
}

/** Called when the app backgrounds, so the next foreground can measure idle. */
export function markAppBackgrounded(now: number = Date.now()): void {
  writeNumber(LAST_ACTIVE_AT_KEY, now);
}

/** Called when the app foregrounds. Starts a session only if idle long enough. */
export function handleAppForegrounded(now: number = Date.now()): void {
  if (shouldStartNewSession(readNumber(LAST_ACTIVE_AT_KEY), now)) {
    trackSessionStarted(now);
  }
}

/**
 * Fire the boot session and wire foreground/background listeners. Safe to call
 * once, from `main.tsx`.
 */
export function initSessionTracking(): void {
  trackSessionStarted();

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") markAppBackgrounded();
      else handleAppForegrounded();
    });
  }

  // Native apps suspend without ever firing `visibilitychange`, so mirror the
  // same transitions onto Capacitor's lifecycle event.
  const cap = (
    globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor;
  if (cap?.isNativePlatform?.()) {
    void import("@capacitor/app")
      .then(({ App: CapacitorApp }) => {
        void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
          if (isActive) handleAppForegrounded();
          else markAppBackgrounded();
        });
      })
      .catch(() => {
        /* native shim unavailable — web listeners still cover it */
      });
  }
}
